import { v4 as uuidv4 } from 'uuid';

import { FieldsetOrientation, InputType } from '../../../shared/constants';
import {
  Tag,
  WizardCard,
  WizardOption,
  WizardStep,
} from '../../../types/shared';

import { getUniqueTags } from '../../../utils/tags';

import { WizardOptionCondition } from '../../../tokens';
import { ParsedResults, WizardItem } from '../../../types/wizard';

const parseStoreSteps = (
  items: WizardStep[] | WizardCard[] | WizardOption[]
): WizardItem[] =>
  items.map((item, index) => {
    const wizardOptionCard = (item as WizardOption).card && [
      (item as WizardOption).card as WizardCard,
    ];
    const itemsInput =
      wizardOptionCard ||
      (item as WizardCard).options ||
      (item as WizardStep).cards ||
      [];

    return {
      id: `${item.id}_${uuidv4()}`,
      contentfulId: item.id,
      value: false,
      textValue: null,
      title: item.title,
      items: parseStoreSteps(itemsInput),
      condition:
        (item as WizardOption).condition || WizardOptionCondition.DEFAULT,
      tracking: {
        name: (item as WizardStep).trackingLabel || item.title || '',
        mainStep: index,
        subStep: 0,
      },
      visual: {
        body: (item as WizardOption).body,
        image: (item as WizardOption).image,
        videoUrl: (item as WizardCard).videoUrl,
        tooltip: (item as WizardOption).tooltip,
        tab: (item as WizardOption).tab,
        results: (item as WizardOption).result,
        resultTags: (item as WizardOption).resultTags,
        itemsAreGrouped: (item as WizardCard).optionsAreGrouped,
        itemsLabel: (item as WizardCard).optionsLabel,
        itemsType: (item as WizardOption).optionsType as InputType,
        itemsHelperMessage: (item as WizardOption).helperMessage,
        itemsOrientation: (item as WizardCard)
          .optionsLayout as FieldsetOrientation,
        accordionLabel: (item as WizardCard).accordionLabel,
        accordionBody: (item as WizardCard).accordionBody,
      },
    };
  });

/* 
  Parse results and devide them into 3 groups.
  
  - regularResults: All results that are found that don't have a text (custom user input) value.
  - textResults: All results that have custom user input.
  - resultTags: All tags that have been found in the results.
*/
const parseResults = (results: WizardItem[]): ParsedResults => {
  const allResults = results.map((result) => ({
    ...result,
    tabId: result.visual?.tab?.id,
  }));
  const allTags = allResults
    .filter((result) => !!result.visual?.resultTags?.length)
    .reduce((accumulator, result) => {
      return [...accumulator, ...(result.visual?.resultTags || [])];
    }, [] as Tag[]);

  return {
    regularResults: allResults.filter((result) => !result.textValue),
    textResults: allResults.filter((result) => !!result.textValue),
    resultTags: getUniqueTags(allTags),
  };
};

const parseResultConditions = (
  results: WizardItem[]
): WizardOptionCondition[] =>
  results
    .map((result) => result.condition)
    .filter(Boolean) as WizardOptionCondition[];

const parseTagsToQueryParameter = (tags: Tag[]): string => {
  const prefix = '&tags=';
  const tagsValues = tags.reduce((accumulator, tag) => {
    const seperator = accumulator !== '' ? ',' : '';

    return `${accumulator}${seperator}${tag.type}_${tag.value}`;
  }, '');

  return tagsValues.length ? `${prefix}${tagsValues}` : '';
};

export {
  parseResultConditions,
  parseResults,
  parseStoreSteps,
  parseTagsToQueryParameter,
};
